import LayoutSettings from '@/components/LayoutSetting/LayoutSettings';
import { PlanMessage } from '@/components/PlanMessage';
import ProductIndexTable from '@/components/ProductIndexTable/ProductIndexTable';
import { AvailabilityType, PricingPlan } from '@/constants/enum';
import {
  deliveryCollectionSelector,
  deliveryDisableSelector,
  deliveryProductSelector,
  deliverySettingSelector,
  deliveryStateSelector,
  handleSaveDeliveryCollections,
  handleSaveDeliveryProducts,
  handleSaveDeliverySettings,
  handleSetDeliveryState,
} from '@/redux/slice/delivery.slice';
import { errorFieldsSelector, handleSetErrorFields } from '@/redux/slice/error.slice';
import {
  BlockStack,
  Box,
  Button,
  ChoiceList,
  Divider,
  InlineError,
  InlineGrid,
  InlineStack,
  RadioButton,
} from '@shopify/polaris';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

export const ProductAvailability = () => {
  const { t } = useTranslation('deliverySettings');
  const deliverySettings = useSelector(deliverySettingSelector);
  const deliveryProducts = useSelector(deliveryProductSelector);
  const deliveryCollections = useSelector(deliveryCollectionSelector);
  const errorFields = useSelector(errorFieldsSelector);
  const deliveryState = useSelector(deliveryStateSelector);
  const deliveryDisable = useSelector(deliveryDisableSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      handleSetErrorFields({
        errorFields: {
          errorSpecificProduct:
            deliveryProducts.length === 0 &&
            deliveryCollections.length === 0 &&
            deliverySettings.productAvailabilityType !== AvailabilityType.ALL,
        },
      }),
    );
  }, [deliverySettings.productAvailabilityType, dispatch, deliveryProducts.length, deliveryCollections.length]);

  const onClickProduct = async () => {
    const selected = await shopify.resourcePicker({
      type: 'product',
      action: 'select',
      selectionIds: deliveryProducts.map((item) => ({ id: `gid://shopify/Product/${item}` })),
      multiple: true,
      filter: {
        hidden: true,
        variants: false,
        draft: false,
        archived: false,
      },
    });

    if (selected) {
      const selectedProducts = selected
        .map((item) => {
          const regex = /gid:\/\/shopify\/Product\/(\d+)/;
          const match = item.id.match(regex);
          if (match) {
            return match[1];
          }
          return null;
        })
        .filter((id): id is string => id !== null);
      dispatch(handleSaveDeliveryProducts(selectedProducts));
      dispatch(
        handleSetErrorFields({
          errorFields: {
            errorSpecificProduct: false,
          },
        }),
      );
    }
  };

  const onClickCollection = async () => {
    const selected = await shopify.resourcePicker({
      type: 'collection',
      action: 'select',
      selectionIds: deliveryCollections.map((item) => ({ id: `gid://shopify/Collection/${item}` })),
      multiple: true,
      filter: {
        hidden: false,
        variants: false,
        draft: false,
        archived: false,
      },
    });

    if (selected) {
      const selectedCollections = selected
        .map((item) => {
          const regex = /gid:\/\/shopify\/Collection\/(\d+)/;
          const match = item.id.match(regex);
          if (match) {
            return match[1];
          }
          return null;
        })
        .filter((id): id is string => id !== null);
      dispatch(handleSaveDeliveryCollections(selectedCollections));
      dispatch(
        handleSetErrorFields({
          errorFields: {
            errorSpecificProduct: false,
          },
        }),
      );
    }
  };

  const handleSetProduct = (products: string[]) => {
    const selectedProducts = products;
    dispatch(handleSaveDeliveryProducts(selectedProducts));
  };

  const handleSetCollection = (collections: string[]) => {
    const selectedCollections = collections;
    dispatch(handleSaveDeliveryCollections(selectedCollections));
  };

  return (
    <>
      <LayoutSettings title={t('sections.productAvailability.title')} helpText={t('sections.productAvailability.helpText')}>
        <BlockStack gap="200">
          <RadioButton
            id="productAvailability-all"
            name="allProductAvailability"
            onChange={() => {
              dispatch(handleSaveDeliverySettings({ productAvailabilityType: AvailabilityType.ALL }));
              dispatch(
                handleSetDeliveryState({
                  isSaving: false,
                }),
              );
            }}
            label={t('sections.productAvailability.allProduct')}
            checked={deliverySettings.productAvailabilityType === AvailabilityType.ALL}
          />
          <InlineStack blockAlign="center" align="space-between">
            <RadioButton
              id="productAvailability-specific"
              name="specificProductAvailability"
              onChange={() => {
                dispatch(handleSaveDeliverySettings({ productAvailabilityType: AvailabilityType.INCLUDED }));
                dispatch(
                  handleSetDeliveryState({
                    isSaving: false,
                  }),
                );
              }}
              label={t('sections.productAvailability.specificProducts.title')}
              checked={
                deliverySettings.productAvailabilityType === AvailabilityType.INCLUDED ||
                deliverySettings.productAvailabilityType === AvailabilityType.EXCLUDED
              }
              disabled={deliveryDisable.isDisableSpecificProduct}
            />
            {deliveryDisable.isDisableSpecificProduct && <PlanMessage plan={PricingPlan.Essential} />}
          </InlineStack>
          {deliverySettings.productAvailabilityType !== AvailabilityType.ALL && (
            <div id="delivery-specific-product">
              <BlockStack gap={'200'}>
                <Box paddingInlineStart="400">
                  <ChoiceList
                    title=""
                    choices={[
                      {
                        label: t('sections.productAvailability.specificProducts.labelInclude'),
                        value: AvailabilityType.INCLUDED,
                        helpText: t('sections.productAvailability.specificProducts.helpTextInclude'),
                      },
                      {
                        label: t('sections.productAvailability.specificProducts.labelExclude'),
                        value: AvailabilityType.EXCLUDED,
                        helpText: t('sections.productAvailability.specificProducts.helpTextExclude'),
                      },
                    ]}
                    selected={[deliverySettings.productAvailabilityType]}
                    onChange={(value) => {
                      dispatch(handleSaveDeliverySettings({ productAvailabilityType: value[0] }));
                    }}
                    disabled={deliveryDisable.isDisableSpecificProduct}
                  />
                </Box>
                <InlineGrid columns={{ xs: 1, md: '1fr' }} alignItems="center" gap="200">
                  {errorFields.errorFields?.errorSpecificProduct && deliveryState.isSaving && (
                    <InlineError message={t('error.productAvailability.validSpecificProduct')} fieldID="errorSpecificProduct" />
                  )}
                  <InlineStack gap="400">
                    <Button onClick={onClickProduct} disabled={deliveryDisable.isDisableSpecificProduct}>
                      Select products
                    </Button>
                    <Button onClick={onClickCollection} disabled={deliveryDisable.isDisableSpecificProduct}>
                      Select collections
                    </Button>
                  </InlineStack>
                </InlineGrid>
                <Divider />
                <ProductIndexTable
                  productIds={deliveryProducts}
                  collectionIds={deliveryCollections}
                  setProductIds={handleSetProduct}
                  setCollectionIds={handleSetCollection}
                  disabledFeature={deliveryDisable.isDisableSpecificProduct}
                />
              </BlockStack>
            </div>
          )}
        </BlockStack>
      </LayoutSettings>
    </>
  );
};
